import React, { useEffect, useState } from "react"
import { ADMIN_TYPE, AdminUser } from "../../models/user"
import { useForm } from "@mantine/form"
import { updateUser } from "../api"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, ScrollArea, Select, Stack } from "@mantine/core"
import { confirmDiscardChanges } from "../utils/confirm"

interface Props {
  opened: boolean
  onClose: () => void
  clientId: number
  user: AdminUser
  onSaved?: () => void
}

interface FormValues {
  admin_type: string
}

const defaultValues: FormValues = {
  admin_type: String(ADMIN_TYPE.GENERAL),
}

const ADMIN_TYPE_OPTIONS = [
  { value: String(ADMIN_TYPE.ADMIN), label: '管理者' },
  { value: String(ADMIN_TYPE.GENERAL), label: '一般' },
]

export const UserAdminTypeUpdateModal: React.FC<Props> = ({ opened, onClose, clientId, user, onSaved }) => {
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      admin_type: (v) => (!v ? '必須です' : null),
    },
  })

  useEffect(() => {
    if (opened) {
      form.setValues({ admin_type: String(user.admin_type) })
      form.resetDirty()
    }
  }, [opened, user])

  const handleClose = () => {
    if (form.isDirty() && !confirmDiscardChanges()) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)

    try {
      await updateUser(clientId, user.id, { admin_type: Number(values.admin_type) })
      notifications.show({ message: '権限を更新しました', color: 'teal' })
      onSaved?.()
      onClose()
    } catch (errors: any) {
      const message = Array.isArray(errors) ? errors.join('\n') : '権限の更新に失敗しました'
      notifications.show({ message, color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title="権限変更"
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            <Select
              label={<><span>管理者権限</span>{requiredMark}</>}
              data={ADMIN_TYPE_OPTIONS}
              {...form.getInputProps('admin_type')}
              allowDeselect={false}
              styles={requireLabelStyles}
            />
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              variant="filled"
              radius="xl"
              type="submit"
              loading={submitting}
            >
              更新する
            </Button>
          </Group>
        </Box>
      </form>
    </BaseModal>
  )
}
